import { Panel } from "@/components/ui/Panel";
import { Field, FormGrid, Input, SaveBar } from "@/components/ui/Form";
import { ErrorState, Skeleton } from "@/components/ui/states";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { comoTexto, dinero, numero, type StoreSettings } from "./storeApi";
import { useSettingsForm } from "./useSettingsForm";

const desdeAjustes = (a: StoreSettings) => ({
  minimum_order: comoTexto(a.minimum_order),
  free_shipping_threshold: comoTexto(a.free_shipping_threshold),
  default_low_stock_threshold: comoTexto(a.default_low_stock_threshold),
  reservation_minutes_online: comoTexto(a.reservation_minutes_online),
  reservation_minutes_manual: comoTexto(a.reservation_minutes_manual),
});

/** Las cifras que deciden cuando se puede comprar y cuanto aguanta el stock. */
export function CommercialPage() {
  const { org } = useActiveOrg();
  const { consulta, borrador, set, sucio, guardando, guardar, descartar } =
    useSettingsForm(desdeAjustes);

  if (consulta.isPending || !borrador) return <Skeleton className="h-96 w-full rounded-lg" />;
  if (consulta.isError) {
    return (
      <ErrorState
        message={friendlyError(consulta.error)}
        onRetry={() => void consulta.refetch()}
      />
    );
  }

  const moneda = org?.currency ?? "PEN";

  return (
    <div className="flex flex-col gap-3">
      <Panel title="Compra mínima y envío">
        <FormGrid>
          <Field
            label={`Monto mínimo de compra (${moneda})`}
            hint="Vacío o 0 = sin mínimo. Se rechaza el pedido que no llegue."
          >
            <Input
              inputMode="decimal"
              value={borrador.minimum_order}
              onChange={(e) => set("minimum_order", e.target.value)}
              placeholder="0.00"
            />
          </Field>
          <Field
            label={`Envío gratis desde (${moneda})`}
            hint="Vacío = no se anuncia envío gratis en la tienda."
          >
            <Input
              inputMode="decimal"
              value={borrador.free_shipping_threshold}
              onChange={(e) => set("free_shipping_threshold", e.target.value)}
              placeholder="Sin umbral"
            />
          </Field>
        </FormGrid>
        <p className="mt-3 text-xs text-ink-3">
          Cada distrito puede tener su propio umbral en Entregas. Este es el general
          de la tienda, el que se muestra en el carrito.
        </p>
      </Panel>

      <Panel title="Inventario">
        <FormGrid>
          <Field
            label="Aviso de stock bajo"
            hint="Unidades a partir de las cuales una variante se marca en amarillo."
          >
            <Input
              inputMode="numeric"
              value={borrador.default_low_stock_threshold}
              onChange={(e) => set("default_low_stock_threshold", e.target.value)}
              placeholder="5"
            />
          </Field>
        </FormGrid>
      </Panel>

      <Panel title="Reserva de stock">
        <FormGrid>
          <Field
            label="Pedido web (minutos)"
            hint="Cuánto se guarda el stock de un pedido sin pagar."
          >
            <Input
              inputMode="numeric"
              value={borrador.reservation_minutes_online}
              onChange={(e) => set("reservation_minutes_online", e.target.value)}
              placeholder="60"
            />
          </Field>
          <Field
            label="Pago manual (minutos)"
            hint="Para Yape o transferencia, que tardan más en verificarse."
          >
            <Input
              inputMode="numeric"
              value={borrador.reservation_minutes_manual}
              onChange={(e) => set("reservation_minutes_manual", e.target.value)}
              placeholder="1440"
            />
          </Field>
        </FormGrid>
        <p className="mt-3 text-xs text-ink-3">
          Pasado ese tiempo sin pago, el stock vuelve al inventario y el pedido queda
          pendiente. Sin esto, un carrito abandonado bloquea producto que otro
          cliente sí quiere comprar.
        </p>
      </Panel>

      <SaveBar
        dirty={sucio}
        saving={guardando}
        onSave={() =>
          void guardar({
            minimum_order: dinero(borrador.minimum_order) ?? "0.00",
            free_shipping_threshold: dinero(borrador.free_shipping_threshold),
            default_low_stock_threshold: numero(borrador.default_low_stock_threshold) ?? 5,
            reservation_minutes_online: numero(borrador.reservation_minutes_online) ?? 60,
            reservation_minutes_manual: numero(borrador.reservation_minutes_manual) ?? 1440,
          })
        }
        onReset={descartar}
      />
    </div>
  );
}
